import type { TFunction } from 'i18next';

// API contract (ADR-0019 in FacilityFlow.Api): lowercase string codes on the wire,
// null = not specified. No enum mapping needed on the client.
export type Salutation = 'mr' | 'ms' | 'diverse';

export const SALUTATIONS: readonly Salutation[] = ['mr', 'ms', 'diverse'] as const;

export function isSalutation(value: unknown): value is Salutation {
  return typeof value === 'string' && (SALUTATIONS as readonly string[]).includes(value);
}

export interface SalutationOption {
  value: Salutation | '';
  label: string;
}

/** Label for the select / badges: "Herr", "Frau", "Divers" or "Keine Angabe" for null. */
export function getSalutationLabel(value: Salutation | null | undefined, t: TFunction): string {
  return isSalutation(value) ? t(`common:salutation.${value}`) : t('common:salutation.none');
}

/** Options for a select, "none" ('') first. */
export function getSalutationOptions(t: TFunction): SalutationOption[] {
  return [
    { value: '', label: t('common:salutation.none') },
    ...SALUTATIONS.map((value) => ({ value, label: t(`common:salutation.${value}`) })),
  ];
}

// why: mirrors SalutationFormatter.Title in the backend — "diverse" and null carry no
// title, so the name is printed bare instead of with an awkward prefix.
function salutationTitle(value: Salutation | null | undefined, t: TFunction): string {
  return value === 'mr' || value === 'ms' ? t(`common:salutation.${value}`) : '';
}

function joinParts(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0)
    .join(' ');
}

/** "Herr Max Müller" — title (if any) + first name + last name. */
export function formatPersonName(
  salutation: Salutation | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  t: TFunction,
): string {
  return joinParts(salutationTitle(salutation, t), firstName, lastName);
}

/** "Frau Eva Muster" — title (if any) + free-text contact person name (offers/invoices). */
export function formatContactPerson(
  salutation: Salutation | null | undefined,
  name: string | null | undefined,
  t: TFunction,
): string {
  return joinParts(salutationTitle(salutation, t), name);
}

/** Narrows an API string (`string | null | undefined`) to a `Salutation`, anything else → null. */
export function toSalutation(value: unknown): Salutation | null {
  return isSalutation(value) ? value : null;
}
